import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  DollarSign, TrendingUp, CreditCard, Banknote, ShoppingBag,
  BarChart3, Tag, Percent, FileDown
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DailyReportTab = ({ adminRole }) => {
  const [report, setReport] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const fetchReport = async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/daily-report?date=${d}`);
      setReport(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(date); }, [date]);

  const formatPrice = (v) => `${(v || 0).toLocaleString()} THB`;
  const isAdmin = adminRole === 'admin';

  if (!report) return <div className="text-center py-12 text-[#6E5E52]">Chargement...</div>;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-[#2D241D]" data-testid="daily-report-title">Rapport Journalier</h3>
          <p className="text-xs text-[#6E5E52]">Cloture de caisse et detail du jour</p>
        </div>
        <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
          className="rounded-xl border-[#E5DCD3] w-44" data-testid="report-date-picker" />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="report-revenue">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-[#0A422D]/10 flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-[#0A422D]" />
            </div>
            <span className="text-xs text-[#6E5E52]">CA Total</span>
          </div>
          <p className="text-xl font-bold text-[#0A422D]">{formatPrice(report.total_revenue)}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="report-orders">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-[#E16833]/10 flex items-center justify-center">
              <ShoppingBag className="w-4 h-4 text-[#E16833]" />
            </div>
            <span className="text-xs text-[#6E5E52]">Commandes</span>
          </div>
          <p className="text-xl font-bold text-[#E16833]">{report.total_orders}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="report-tips">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-[#D4A853]/10 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-[#D4A853]" />
            </div>
            <span className="text-xs text-[#6E5E52]">Pourboires</span>
          </div>
          <p className="text-xl font-bold text-[#D4A853]">{formatPrice(report.total_tips)}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="report-discounts">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-8 h-8 rounded-lg bg-[#E63946]/10 flex items-center justify-center">
              <Tag className="w-4 h-4 text-[#E63946]" />
            </div>
            <span className="text-xs text-[#6E5E52]">Remises</span>
          </div>
          <p className="text-xl font-bold text-[#E63946]">{formatPrice(report.total_discount)}</p>
        </div>
      </div>

      {/* Cash vs Stripe */}
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
        <h4 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-[#0A422D]" /> Repartition des paiements
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-[#F3EDE5] rounded-xl p-4 text-center" data-testid="report-cash">
            <Banknote className="w-6 h-6 mx-auto mb-1 text-[#2D6A4F]" />
            <p className="text-xs text-[#6E5E52]">Cash</p>
            <p className="text-lg font-bold text-[#2D6A4F]">{formatPrice(report.cash_revenue)}</p>
          </div>
          <div className="bg-[#F3EDE5] rounded-xl p-4 text-center" data-testid="report-stripe">
            <CreditCard className="w-6 h-6 mx-auto mb-1 text-[#6366F1]" />
            <p className="text-xs text-[#6E5E52]">CB / Stripe</p>
            <p className="text-lg font-bold text-[#6366F1]">{formatPrice((report.card_terminal_revenue || 0) + (report.stripe_revenue || 0))}</p>
          </div>
        </div>
        {report.total_revenue > 0 && (
          <div className="mt-3 bg-[#E5DCD3] rounded-full h-3 overflow-hidden">
            <div className="h-full bg-[#2D6A4F] rounded-l-full"
              style={{ width: `${(report.cash_revenue / report.total_revenue) * 100}%` }} />
          </div>
        )}
      </div>

      {/* Margins (admin only) */}
      {isAdmin && (
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid="report-margins">
          <h4 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
            <Percent className="w-4 h-4 text-[#D4A853]" /> Marges
          </h4>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-[#FFF3E0] rounded-xl p-3 text-center">
              <p className="text-xs text-[#6E5E52]">Cout total</p>
              <p className="text-lg font-bold text-[#E16833]">{formatPrice(report.total_cost)}</p>
            </div>
            <div className="bg-[#E8F5E9] rounded-xl p-3 text-center">
              <p className="text-xs text-[#6E5E52]">Marge brute</p>
              <p className="text-lg font-bold text-[#2D6A4F]">{formatPrice(report.gross_margin)}</p>
            </div>
            <div className="bg-[#E8F5E9] rounded-xl p-3 text-center">
              <p className="text-xs text-[#6E5E52]">% Marge</p>
              <p className="text-lg font-bold text-[#2D6A4F]">{report.margin_percent}%</p>
            </div>
          </div>
        </div>
      )}

      {/* Category breakdown */}
      {report.category_breakdown?.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <h4 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#E16833]" /> Ventes par categorie
          </h4>
          <div className="space-y-2">
            {report.category_breakdown.map((cat, idx) => {
              const maxRev = report.category_breakdown[0]?.revenue || 1;
              return (
                <div key={idx} className="flex items-center gap-3" data-testid={`cat-breakdown-${idx}`}>
                  <div className="flex-1">
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#2D241D] font-medium">{cat.category_name}</span>
                      <span className="text-[#6E5E52]">{formatPrice(cat.revenue)} ({cat.items_sold} art.)</span>
                    </div>
                    <div className="w-full bg-[#F3EDE5] rounded-full h-2">
                      <div className="bg-gradient-to-r from-[#D4A853] to-[#E16833] h-2 rounded-full"
                        style={{ width: `${(cat.revenue / maxRev) * 100}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top dishes of the day */}
      {report.top_dishes?.length > 0 && (
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <h4 className="font-semibold text-[#2D241D] mb-3 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#D4A853]" /> Top plats du jour
          </h4>
          <div className="space-y-2">
            {report.top_dishes.map((dish, idx) => (
              <div key={idx} className="flex items-center justify-between bg-[#F3EDE5] rounded-xl px-3 py-2" data-testid={`top-dish-day-${idx}`}>
                <div className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-[10px] font-bold text-[#6E5E52]">{idx + 1}</span>
                  <span className="text-sm text-[#2D241D]">{dish.name}</span>
                </div>
                <span className="text-sm font-bold text-[#E16833]">{dish.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
